<template>
  <Modal
    v-model="visibleSync"
    :title="modalTitle"
    class-name="event-trigger-modal"
    width="620"
    @on-visible-change="onVisibleChange"
  >
    <div class="event-trigger-modal-form">
      <div class="event-trigger-modal-field">
        <label class="event-trigger-modal-field__label">
          <span>
            触发条件
            <span class="event-trigger-modal-field__required">*</span>
          </span>
          <span class="event-trigger-modal-field__hint">
            只要满足一个条件就行。
          </span>
        </label>
        <div class="event-trigger-cond-list">
          <div
            v-for="(row, index) in form.conditions"
            :key="'cond-' + index"
            class="event-trigger-cond-row"
          >
            <Select
              v-model="row.field"
              class="event-trigger-cond-row__field"
              transfer
              @on-change="onConditionFieldChange(row)"
            >
              <Option
                v-for="opt in fieldOptions"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              />
            </Select>
            <Select
              v-model="row.op"
              class="event-trigger-cond-row__op"
              transfer
              :disabled="isOpLocked(row)"
            >
              <Option
                v-for="opt in getOpsForRow(row)"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              />
            </Select>
            <Select
              v-if="row.field === FIELD.INTENT_NAME"
              v-model="row.value"
              class="event-trigger-cond-row__value"
              transfer
              :placeholder="intentNamePlaceholder"
            >
              <Option
                v-for="name in getIntentNameOptions(row)"
                :key="name"
                :value="name"
                :label="name"
              />
            </Select>
            <Input
              v-else
              v-model="row.value"
              class="event-trigger-cond-row__value"
              :placeholder="getValuePlaceholder(row)"
              :maxlength="50"
            />
            <span
              class="event-trigger-cond-row__del"
              :class="{ 'is-disabled': form.conditions.length <= 1 }"
              @click="removeConditionRow(index)"
            >
              <Icon type="md-close" size="18" />
            </span>
          </div>
        </div>
        <Button type="text" class="event-trigger-modal-add-row" @click="addConditionRow">
          + 添加条件
        </Button>
      </div>

      <div class="event-trigger-modal-field">
        <label class="event-trigger-modal-field__label">
          动作
          <span class="event-trigger-modal-field__required">*</span>
        </label>
        <div class="event-trigger-action-row" :class="actionRowClass">
          <Select
            v-model="form.action"
            class="event-trigger-action-row__type"
            transfer
            placeholder="请选择"
            @on-change="onActionChange"
          >
            <Option
              v-for="opt in actionOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
            />
          </Select>
          <Select
            v-if="form.action === ACTION.API_CALL"
            v-model="form.api_id"
            class="event-trigger-action-row__api"
            transfer
            placeholder="请选择"
          >
            <Option
              v-for="opt in apiOptions"
              :key="opt.id"
              :value="opt.id"
              :label="formatApiOptionLabel(opt)"
            />
          </Select>
          <Select
            v-if="form.action === ACTION.COLLAB_REMIND"
            v-model="form.collab_seat_type"
            class="event-trigger-action-row__seat"
            transfer
            placeholder="请选择"
            @on-change="onCollabSeatTypeChange"
          >
            <Option
              v-for="opt in collabSeatOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
            />
          </Select>
          <div
            v-if="showCollabTarget"
            class="event-trigger-action-row__target"
          >
            <span class="event-trigger-action-row__target-label">目标:</span>
            <Input
              v-model="form.collab_target"
              class="event-trigger-action-row__target-input"
              placeholder="请输入目标"
              :maxlength="50"
            />
          </div>
        </div>

        <div v-if="form.action === ACTION.SMS" class="event-trigger-sms-panel">
          <div class="event-trigger-sms-row">
            <label class="event-trigger-sms-row__label">短信通道:</label>
            <Select v-model="form.sms_channel_id" class="event-trigger-sms-row__control" transfer placeholder="请选择短信通道">
              <Option
                v-for="ch in smsChannels"
                :key="ch.id"
                :value="ch.id"
                :label="ch.name"
              />
            </Select>
          </div>
          <div class="event-trigger-sms-row event-trigger-sms-row--top">
            <label class="event-trigger-sms-row__label">参数内容:</label>
            <Input
              v-model="form.sms_params"
              type="textarea"
              :rows="4"
              class="event-trigger-sms-row__control"
              placeholder="请输入参数内容"
            />
          </div>
        </div>
      </div>

      <div class="event-trigger-modal-field event-trigger-modal-field--once">
        <label class="event-trigger-modal-field__label event-trigger-modal-field__label--inline">
          只执行一次
        </label>
        <i-switch v-model="form.once_only" />
      </div>
    </div>

    <div slot="footer" class="event-trigger-modal-footer">
      <Button @click="close">取消</Button>
      <Button type="primary" @click="submit">确定</Button>
    </div>
  </Modal>
</template>

<script>
import {
  EVENT_TRIGGER_ACTION,
  EVENT_TRIGGER_ACTION_OPTIONS,
  EVENT_TRIGGER_COLLAB_SEAT,
  EVENT_TRIGGER_COLLAB_SEAT_OPTIONS,
  EVENT_TRIGGER_FIELD,
  EVENT_TRIGGER_FIELD_OPTIONS,
  EVENT_TRIGGER_OP,
  EVENT_TRIGGER_SMS_CHANNELS,
} from '../../constants/eventTrigger'
import {
  createDefaultEventTrigger,
  createDefaultEventTriggerCondition,
  getOpsForEventTriggerField,
  isEventTriggerOpLocked,
  normalizeEventTrigger,
} from '../../utils/eventTriggerHelpers'

function cloneConditions(conditions) {
  return (Array.isArray(conditions) ? conditions : []).map((row) => ({ ...row }))
}

export default {
  name: 'EventTriggerModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editIndex: {
      type: Number,
      default: -1,
    },
    initialData: {
      type: Object,
      default: null,
    },
    apiOptions: {
      type: Array,
      default: () => [],
    },
    intentTagNameOptions: {
      type: Array,
      default: () => [],
    },
    intentNamePlaceholder: {
      type: String,
      default: '请选择意向标签',
    },
  },
  data() {
    return {
      ACTION: EVENT_TRIGGER_ACTION,
      FIELD: EVENT_TRIGGER_FIELD,
      fieldOptions: EVENT_TRIGGER_FIELD_OPTIONS,
      actionOptions: EVENT_TRIGGER_ACTION_OPTIONS,
      smsChannels: EVENT_TRIGGER_SMS_CHANNELS,
      collabSeatOptions: EVENT_TRIGGER_COLLAB_SEAT_OPTIONS,
      form: createDefaultEventTrigger(),
    }
  },
  computed: {
    visibleSync: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    modalTitle() {
      return this.editIndex >= 0 ? '编辑触发器' : '添加触发器'
    },
    showCollabTarget() {
      return (
        this.form.action === EVENT_TRIGGER_ACTION.COLLAB_REMIND &&
        this.form.collab_seat_type === EVENT_TRIGGER_COLLAB_SEAT.THIRD_PARTY
      )
    },
    actionRowClass() {
      if (this.form.action === EVENT_TRIGGER_ACTION.API_CALL) return 'has-api'
      if (this.showCollabTarget) return 'has-collab-target'
      if (this.form.action === EVENT_TRIGGER_ACTION.COLLAB_REMIND) return 'has-collab'
      return ''
    },
  },
  methods: {
    onVisibleChange(visible) {
      if (visible) {
        this.resetForm()
      }
    },
    resetForm() {
      const item = this.initialData
        ? normalizeEventTrigger(this.initialData)
        : createDefaultEventTrigger()
      this.form = {
        id: item.id,
        conditions: cloneConditions(item.conditions),
        action: item.action || undefined,
        api_id: item.api_id || undefined,
        sms_channel_id: item.sms_channel_id || undefined,
        sms_params: item.sms_params,
        collab_seat_type: item.collab_seat_type || undefined,
        collab_target: item.collab_target,
        once_only: item.once_only !== false,
      }
    },
    getOpsForRow(row) {
      return getOpsForEventTriggerField(row.field)
    },
    isOpLocked(row) {
      return isEventTriggerOpLocked(row && row.field)
    },
    getValuePlaceholder(row) {
      if (row.field === EVENT_TRIGGER_FIELD.CALL_DURATION) {
        return '单位：秒，如 30'
      }
      if (row.field === EVENT_TRIGGER_FIELD.FUNCTION_NAME) {
        return '请输入函数名'
      }
      return this.intentNamePlaceholder
    },
    getIntentNameOptions(row) {
      const names = Array.isArray(this.intentTagNameOptions) ? this.intentTagNameOptions.slice() : []
      const current = row && row.value != null ? String(row.value).trim() : ''
      if (current && names.indexOf(current) === -1) {
        names.unshift(current)
      }
      return names
    },
    onConditionFieldChange(row) {
      const ops = getOpsForEventTriggerField(row.field)
      row.op = ops[0] ? ops[0].value : EVENT_TRIGGER_OP.EQ
      row.value = ''
    },
    addConditionRow() {
      this.form.conditions.push(createDefaultEventTriggerCondition())
    },
    removeConditionRow(index) {
      if (this.form.conditions.length <= 1) return
      this.form.conditions.splice(index, 1)
    },
    onActionChange() {
      if (this.form.action !== EVENT_TRIGGER_ACTION.API_CALL) {
        this.form.api_id = undefined
      }
      if (this.form.action !== EVENT_TRIGGER_ACTION.SMS) {
        this.form.sms_channel_id = undefined
        this.form.sms_params = ''
      }
      if (this.form.action !== EVENT_TRIGGER_ACTION.COLLAB_REMIND) {
        this.form.collab_seat_type = undefined
        this.form.collab_target = ''
      }
    },
    onCollabSeatTypeChange() {
      if (this.form.collab_seat_type !== EVENT_TRIGGER_COLLAB_SEAT.THIRD_PARTY) {
        this.form.collab_target = ''
      }
    },
    formatApiOptionLabel(opt) {
      if (!opt) return ''
      return `${opt.label}${opt.source === 'json' ? '（JSON）' : '（表单）'}`
    },
    close() {
      this.visibleSync = false
    },
    submit() {
      for (let i = 0; i < this.form.conditions.length; i += 1) {
        const row = this.form.conditions[i]
        if (!String(row.op || '').trim()) {
          this.$Message.warning(`请选择第 ${i + 1} 条条件的运算符`)
          return
        }
        if (!String(row.value || '').trim()) {
          this.$Message.warning(
            row.field === EVENT_TRIGGER_FIELD.INTENT_NAME
              ? `请选择第 ${i + 1} 条条件的值`
              : `请填写第 ${i + 1} 条条件的值`,
          )
          return
        }
      }

      if (!this.form.action) {
        this.$Message.warning('请选择动作')
        return
      }
      if (this.form.action === EVENT_TRIGGER_ACTION.API_CALL && !String(this.form.api_id || '').trim()) {
        this.$Message.warning('请选择调用接口')
        return
      }
      if (this.form.action === EVENT_TRIGGER_ACTION.SMS && !String(this.form.sms_channel_id || '').trim()) {
        this.$Message.warning('请选择短信通道')
        return
      }
      if (this.form.action === EVENT_TRIGGER_ACTION.SMS && !String(this.form.sms_params || '').trim()) {
        this.$Message.warning('请填写短信参数内容')
        return
      }
      if (this.form.action === EVENT_TRIGGER_ACTION.COLLAB_REMIND && !String(this.form.collab_seat_type || '').trim()) {
        this.$Message.warning('请选择协同提醒坐席类型')
        return
      }
      if (
        this.form.action === EVENT_TRIGGER_ACTION.COLLAB_REMIND &&
        this.form.collab_seat_type === EVENT_TRIGGER_COLLAB_SEAT.THIRD_PARTY &&
        !String(this.form.collab_target || '').trim()
      ) {
        this.$Message.warning('请输入协同提醒目标')
        return
      }

      this.$emit('submit', normalizeEventTrigger(this.form))
      this.visibleSync = false
    },
  },
}
</script>

<style lang="scss" scoped>
.event-trigger-modal-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.event-trigger-modal-field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.event-trigger-modal-field--once {
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.event-trigger-modal-field__label {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #262626;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.event-trigger-modal-field__label--inline {
  margin-bottom: 0;
}

.event-trigger-modal-field__required {
  margin-left: 2px;
  color: #ed4014;
}

.event-trigger-modal-field__hint {
  font-size: 12px;
  font-weight: 400;
  color: #8c8c8c;
  line-height: 1.5;
}

.event-trigger-cond-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.event-trigger-cond-row {
  display: grid;
  grid-template-columns: 1fr 108px 1fr 28px;
  gap: 8px;
  align-items: center;
}

.event-trigger-cond-row__del {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  color: #c5c8ce;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover:not(.is-disabled) {
    color: #ed4014;
    background: #fff1f0;
  }

  &.is-disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.event-trigger-modal-add-row {
  align-self: flex-start;
  display: inline-flex !important;
  width: auto !important;
  margin-top: 8px;
  padding: 2px 8px !important;
  height: auto !important;
  border-radius: 4px !important;
  color: #5668f0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not([disabled]):not(.ivu-btn-disabled),
  &:focus:not([disabled]):not(.ivu-btn-disabled) {
    color: #3f51e8 !important;
    background: #f5f7ff !important;
  }
}

.event-trigger-action-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;

  &.has-api,
  &.has-collab {
    grid-template-columns: 1fr 1fr;
  }

  &.has-collab-target {
    grid-template-columns: minmax(0, 1fr) 120px minmax(160px, 1.3fr);
  }
}

.event-trigger-action-row__target {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 6px;
}

.event-trigger-action-row__target-label {
  flex-shrink: 0;
  font-size: 14px;
  color: #262626;
  white-space: nowrap;
}

.event-trigger-action-row__target-input {
  flex: 1;
  min-width: 0;
}

.event-trigger-sms-panel {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-trigger-sms-row {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 8px;
  align-items: center;

  &--top {
    align-items: flex-start;

    .event-trigger-sms-row__label {
      margin-top: 6px;
    }
  }
}

.event-trigger-sms-row__label {
  font-size: 14px;
  color: #262626;
  white-space: nowrap;
}

.event-trigger-sms-row__control {
  width: 100%;
}

.event-trigger-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
</style>

<style lang="scss">
.event-trigger-modal {
  .ivu-modal-body {
    padding: 20px 24px 8px;
  }

  .ivu-modal-footer {
    border-top: none;
    padding: 8px 24px 20px;

    button + button {
      margin-left: 0;
    }
  }
}
</style>
